import React from "react";
import { Chip, Stack } from "@mui/material";
import { QuickReply } from "../utils/types";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

interface MultiSelectChipsProps {
  chips: QuickReply[];
  selectedChips: string[];
  onSelectionChange: (selectedChips: QuickReply[]) => void;
  disabled?: boolean;
}

const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({ chips, selectedChips, onSelectionChange, disabled }) => {
  const { containerRef, isDragging, isOverflowing, dragHandlers, centerScroll } = useHorizontalDragScroll();
  const chipsSignature = React.useMemo(
    () => chips.map((chip) => `${chip.title}|${chip.payload ?? ""}`).join("||"),
    [chips]
  );

  React.useEffect(() => {
    const rafId = window.requestAnimationFrame(() => centerScroll());
    return () => window.cancelAnimationFrame(rafId);
  }, [centerScroll, chipsSignature]);

  const handleChipClick = (chip: QuickReply) => {
    if (disabled) return;
    let newSelectedChipsTitles: string[];
    if (selectedChips.includes(chip.title)) {
      newSelectedChipsTitles = selectedChips.filter((t) => t !== chip.title);
    } else {
      newSelectedChipsTitles = [...selectedChips, chip.title];
    }
    
    // Map back to QuickReply objects for the callback
    const newSelectedChips = chips.filter(c => newSelectedChipsTitles.includes(c.title));
    onSelectionChange(newSelectedChips);
  };

  return (
    <div className="suggestion-chips-wrapper">
      <Stack
        ref={containerRef}
        direction="row"
        spacing={1}
        className={`suggestion-chips${isDragging ? " is-dragging" : ""}${isOverflowing ? " is-overflowing" : ""}`}
        sx={{ flexWrap: "nowrap", overflowX: "auto" }}
        {...dragHandlers}
      >
        {chips.map((chip, idx) => {
          const isSelected = selectedChips.includes(chip.title);
          return (
            <Chip
              key={idx}
              label={chip.title}
              onClick={() => handleChipClick(chip)}
              variant={isSelected ? "filled" : "outlined"}
              clickable={!disabled}
              disabled={disabled}
              sx={{
                background: isSelected ? "var(--primary)" : "rgba(255, 255, 255, 0.1)",
                boxShadow: "0 3px 10px rgba(0, 0, 6, 0.3)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                borderRadius: "16px",
                border: isSelected ? "1px solid var(--primary)" : "1px solid rgba(255, 255, 255, 0.2)",
                color: isSelected ? "#ffffff" : "var(--text-dark)",
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? "none" : "auto",
                "&:hover": {
                   background: isSelected ? "var(--primary-dark)" : "rgba(255, 255, 255, 0.2)",
                }
              }}
            />
          );
        })}
      </Stack>
    </div>
  );
};

export default MultiSelectChips;
